import { apiConfig } from '@/shared/config/api.config';
import {
  getAntiForgeryHeadersForMutatingRequest,
  rememberTokenFromResponseHeaders,
} from './abpAntiforgery';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestConfig {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: unknown;
}

/** ABP-style error body for user-facing messages */
interface AbpErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: string;
    validationErrors?: Array<{ message?: string; members?: string[] }>;
  };
  error_description?: string;
}

function parseJsonSafe(text: string): { ok: true; value: unknown } | { ok: false } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: true, value: undefined };
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch {
    return { ok: false };
  }
}

function extractAbpMessage(body: unknown): string | undefined {
  const b = body as AbpErrorBody | undefined;
  if (b?.error?.message) {
    const ve = b.error.validationErrors;
    if (Array.isArray(ve) && ve.length > 0) {
      const parts = ve
        .map((v) => (v?.message ? String(v.message).trim() : ''))
        .filter(Boolean);
      if (parts.length) return [b.error.message, ...parts].join(' — ');
    }
    return b.error.message;
  }
  if (b?.error_description) return b.error_description;
  return undefined;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Prefer ABP error message for display (validation, unauthorized, etc.). */
  static getUserMessage(err: ApiError): string {
    const b = err.body as AbpErrorBody | undefined;
    if (b?.error?.message) return b.error.message;
    if (b?.error_description) return b.error_description;
    return err.message;
  }
}

/**
 * Single HTTP client for all backend calls. Handles base URL, JSON, and errors.
 * Uses credentials: 'include' for cookie-based auth (e.g. ABP session).
 */
function isMutatingMethod(method: HttpMethod): boolean {
  return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
}

function isRedirectStatus(status: number): boolean {
  return status === 301 || status === 302 || status === 303 || status === 307 || status === 308;
}

async function request<T>(path: string, config: RequestConfig = {}): Promise<T> {
  const { method = 'GET', headers = {}, body } = config;
  const url = path.startsWith('http') ? path : `${apiConfig.baseUrl}${path}`;
  const mutating = isMutatingMethod(method);

  const requestHeaders: Record<string, string> = {
    ...(mutating ? getAntiForgeryHeadersForMutatingRequest() : {}),
    ...headers,
  };
  /** Only send JSON content type when there is a body; GET/DELETE with Content-Type can break APIs/proxies */
  if (body != null) {
    requestHeaders['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: requestHeaders,
      credentials: 'include',
      cache: 'no-store',
      /** Avoid following 302 to HTML /Error (breaks JSON parsing); see redirect branch below. */
      redirect: mutating ? 'manual' : 'follow',
      ...(body != null && { body: JSON.stringify(body) }),
    });
  } catch (e) {
    console.error('Fetch FAILED', { url, error: e });
    throw e;
  }

  if (mutating && isRedirectStatus(res.status)) {
    const location = res.headers.get('Location')?.trim() ?? '';
    throw new ApiError(
      [
        'The API responded with a redirect instead of JSON (common causes: antiforgery failure or validation error page).',
        'If the SPA is on a different host than the API, ensure CORS exposes antiforgery headers so the client can read them (e.g. Access-Control-Expose-Headers: Abp-RequestVerificationToken, RequestVerificationToken, X-XSRF-TOKEN) in addition to AllowCredentials and your origin.',
        location ? `Location: ${location}` : '',
      ]
        .filter(Boolean)
        .join(' '),
      res.status,
      { location }
    );
  }

  rememberTokenFromResponseHeaders(res.headers);

  if (!res.ok) {
    const text = await res.text();
    let parsedBody: unknown;
    let message = `API error: ${res.status}`;
    const parsed = parseJsonSafe(text);
    if (parsed.ok) {
      parsedBody = parsed.value;
      const abpMsg = extractAbpMessage(parsed.value);
      if (abpMsg) message = abpMsg;
      else if (
        parsed.value &&
        typeof parsed.value === 'object' &&
        'error' in parsed.value
      ) {
        message += ` — ${String((parsed.value as { error: unknown }).error)}`;
      }
    } else {
      parsedBody = text;
      const snippet = text.trim().slice(0, 500);
      if (snippet) message = `${message}: ${snippet}`;
    }
    throw new ApiError(message, res.status, parsedBody);
  }

  const text = await res.text();
  const parsed = parseJsonSafe(text);
  if (!parsed.ok) {
    const snippet = text.trim().slice(0, 200);
    throw new ApiError(
      `Invalid or non-JSON response (${res.status})${snippet ? `: ${snippet}` : ''}`,
      res.status,
      text
    );
  }
  if (parsed.value === undefined) return undefined as T;
  return parsed.value as T;
}


export const httpClient = {
  get<T>(path: string, config?: Pick<RequestConfig, 'headers'>): Promise<T> {
    return request<T>(path, { method: 'GET', ...config });
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body });
  },
  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PUT', body });
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body });
  },
  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' });
  },
};
